import { z } from 'zod'

/**
 * concept-graph.json — quinta entry OPZIONALE del bundle .arianna (TASK-016).
 *
 * Mappa concettuale del manuale a 2 livelli persistiti (macro-tema + concetto),
 * più un terzo livello (blocco) referenziato via `block_id` ma NON materializzato
 * come nodo (i blocchi vivono già in manuale.json/block-structure.json).
 *
 * Generata dallo stage `concept-graph` (packages/core/src/pipeline/concept-graph.ts)
 * a partire dai blocchi dei sotto-paragrafi del manuale — MAI dalle card. Pattern
 * di retrocompatibilità identico a block-structure.json: l'entry è opzionale sia
 * in lettura (bundle senza grafo restano validi) sia come artefatto (un progetto
 * senza lo stage concept-graph eseguito non ha `graph.json`).
 */

// ── RelationType — enum chiuso a 7 valori (§ Ontologia, TASK-016) ─────────────
// Solo 'opposes' è simmetrico (direzione non significativa); gli altri 6 sono
// direzionali. Questa lista è la fonte unica riusata sia dallo schema zod sotto
// sia dal prompt LLM che deve scegliere tra questi 7 valori esatti (mai inventarne
// altri) — importata da `packages/core` per costruire il testo del prompt.
export const RELATION_TYPES = [
  'is-a',
  'part-of',
  'causes',
  'prerequisite-of',
  'exemplifies',
  'defined-by',
  'opposes',
] as const

export const RelationTypeSchema = z.enum(RELATION_TYPES)
export type RelationType = z.infer<typeof RelationTypeSchema>

/** Relazioni simmetriche (direzione non significativa): endpoint ordinati per idempotenza. */
export const SYMMETRIC_RELATION_TYPES: readonly RelationType[] = ['opposes']

export const ConceptGraphLevelSchema = z.enum(['macro', 'meso'])
export type ConceptGraphLevel = z.infer<typeof ConceptGraphLevelSchema>

export const ConceptGraphScopeSchema = z.enum(['intra', 'cross', 'macro'])
export type ConceptGraphScope = z.infer<typeof ConceptGraphScopeSchema>

export const ConceptNodeSchema = z
  .object({
    nodeId: z.string().min(1),
    level: ConceptGraphLevelSchema,
    label: z.string().min(1),
    canonicalName: z.string().min(1),
    /** meso → nodeId del macro-tema; macro → null. */
    parentNodeId: z.string().nullable(),
    /** Definizione/riassunto in 1 riga. Può essere vuota per i nodi macro. */
    summary: z.string(),
    /**
     * block_id dei sotto-paragrafi che giustificano il nodo. ≥1 per i nodi meso
     * (invariante di evidenza, applicata in codice — non nello schema, perché lo
     * stesso schema serve anche ai nodi macro, per cui può essere []).
     */
    sourceBlockIds: z.array(z.string().min(1)),
    /** macroId del capitolo di appartenenza (per i nodi macro, il proprio id). */
    chapterId: z.string().min(1),
    /**
     * Organizzatore interno d'esplosione opzionale (sotto-tema). `nullish()`
     * (non solo `optional()`) perché Rails serializza SEMPRE la chiave con
     * valore `null` quando assente (colonna nullable, vedi
     * `concept_graph_query.rb`): il round-trip pipeline→bundle→ingest→serving
     * deve accettare sia l'assenza della chiave (costruzione nel core) sia il
     * `null` esplicito (payload Rails), come `parentNodeId` sotto.
     */
    subThemeId: z.string().nullish(),
  })
  .strict()
export type ConceptNode = z.infer<typeof ConceptNodeSchema>

export const ConceptEdgeSchema = z
  .object({
    edgeId: z.string().min(1),
    sourceNodeId: z.string().min(1),
    targetNodeId: z.string().min(1),
    relationType: RelationTypeSchema,
    /** false SOLO per 'opposes' (relazione simmetrica). */
    directed: z.boolean(),
    scope: ConceptGraphScopeSchema,
    /** block_id che giustificano l'arco. Invariante di evidenza applicata in codice. */
    evidenceBlockIds: z.array(z.string().min(1)),
    confidence: z.number().min(0).max(1),
    /** Peso da rilevanza d'esame, a granularità sotto-paragrafo. 0 = nessuna co-occorrenza. */
    examWeight: z.number().min(0).max(1),
  })
  .strict()
export type ConceptEdge = z.infer<typeof ConceptEdgeSchema>

export const CONCEPT_GRAPH_SCHEMA_VERSION = 1 as const

export const ConceptGraphSchema = z
  .object({
    schemaVersion: z.literal(CONCEPT_GRAPH_SCHEMA_VERSION),
    nodes: z.array(ConceptNodeSchema),
    edges: z.array(ConceptEdgeSchema),
  })
  .strict()
export type ConceptGraph = z.infer<typeof ConceptGraphSchema>
